<template lang="pug">
    Panel(panelClassName="targeted-areas")
        template(slot="header")
            sectionHeader(title="targeted areas")
                template(slot="action")
                    div.section-header--action.toto
                        div.section-header--action-item.action-item-direct.l-flex-row
                            label(
                                for="uploadGeoradar"
                                title="upload a csv file"
                                class="label_uploadGeoradar"
                                :class="{ disabled: loading }"
                            )
                                spinner(v-if="checkIsLoading" width="20" height="20")
                                svg-icon(v-else name="upload" size="medium")
                                input(
                                    id="uploadGeoradar" 
                                    class="inputfile"
                                    ref="uploadGeoradar"
                                    type="file" 
                                    accept=".csv"
                                    :disabled="loading"
                                    @change="importGeoradar($event)")
                            label(
                                title="download existing point in csv"
                                class="label_uploadGeoradar"
                                :class="{ disabled: loading }"
                                @click="exportGeoradarCSV()"
                            )
                                spinner(v-if="exportLoading" width="20" height="20")
                                svg-icon(v-else name="download" size="medium")
                            button.btn-border.btn--small.btn--round.l-flex-row.l-align-center(
                                title="Add positions"
                                :disabled="loading"
                                @click="openNewForm()") + New


        template(slot="content")

            //- MODAL
            GeoradarForm()

            //- ZERO STATE
            div.no-data(v-if="pointsAreReady && zeroState && !formAction")
                svg-icon(name='ui-arrow-top-right' size='custom')
                p Add a position
                    br
                    | or
                    br 
                    | upload a csv file.
            
            //- TABS

            div.l-flex-row--rev.l-align-center(
                v-show="!pointsAreReady || (!zeroState && !formAction)"
            )
                div.counter-wrapper.l-flex-row
                    div.info-counter.l-flex-row(v-for="(count, action) in actionsCount" :key="action")
                        div.counter-label(:class="`counter-${action}`") {{ action }} - {{ count }}
            
            GeoradarPointsList(
                v-show="!pointsAreReady || (!zeroState && !formAction)"
                :points="points"
                :loading="!pointsAreReady"
                @handleClickOnPoint="handleClickOnPoint"
            )

            div.l-flex-row--center.l-align-center.save-georadar-point(
                v-show="!pointsAreReady || (!zeroState && !formAction)"
            )
                button.btn-simple.medium(
                    :disabled="disableCancelButton"
                    @click="handleCancel()"
                )
                    span Cancel
                button.btn-simple.btn-rev.medium(
                    title="Save points"
                    :disabled="disableSaveButton"
                    @click="saveCheckedGeoradarPoints()"
                )
                    span Save
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { head, isEmpty } from 'lodash'

import Panel from '@/components/common/Panel'
import SectionHeader from '@/components/common/SectionHeader'
import Tabs from '@/components/common/Tabs'

import GeoradarForm from '@/components/onBoarding/GeoradarForm'
import GeoradarPointsList from '@/components/onBoarding/GeoradarPointsList'

import { GEORADAR_DATATABS } from '@/utils/constants'
import { EMPTY_POINT } from '@/store/ui/georadar/helpers'

export default {
    name: 'GeoradarPanel',

    components: {
        GeoradarForm,
        GeoradarPointsList,
        Panel,
        SectionHeader,
        Tabs
    },

    data() {
        return {
            dataTabs: GEORADAR_DATATABS,
            exportLoading: false
        }
    },

    computed: {
        ...mapState('ui/georadar', {
            formAction: state => state.form.action,
            focusedPoint: state => state.focusedPoint
        }),
        ...mapState('data/georadar', {
            points: state => state.unSavedPoints,
            savedPoints: state => state.savedPoints,
            checkIsLoading: state => state.checkIsLoading,
            retrievePointsLoading: state => state.retrievePointsLoading,
            retrievePointsCount: state => state.retrievePointsCount
        }),
        pointsAreReady() {
            return !this.retrievePointsLoading && this.retrievePointsCount > 0
        },
        loading() {
            return !this.pointsAreReady || this.checkIsLoading
        },
        zeroState() {
            return isEmpty(this.points)
        },
        pointWithAction() {
            return this.points.filter(p => p.action)
        },
        actionsCount() {
            return {
                created: this.points.filter(p => p.action === 'create').length,
                updated: this.points.filter(p => p.action === 'update').length,
                deleted: this.points.filter(p => p.action === 'delete').length
            }
        },
        disableCancelButton() {
            return this.loading || isEmpty(this.pointWithAction)
        },
        disableSaveButton() {
            return this.disableCancelButton || this.points.some(p => p.errors)
        }
    },

    methods: {
        ...mapMutations('data/georadar', ['syncPoints']),
        ...mapMutations('ui/georadar', ['setFocusedPoint', 'setFormAction', 'setForm']),
        ...mapActions('data/georadar', ['getSavedPointsToCSV', 'importGeoradarCSV', 'updateGeoradarPoint', 'saveGeoradarPoints']),

        openNewForm() {
            if (this.formAction !== 'create') {
                this.setForm({ ...EMPTY_POINT })
                this.setFormAction('create')
            }
        },

        handleClickOnPoint({ index }) {
            if (index === this.focusedPoint.index) {
                this.setFocusedPoint({ index: null, shouldFly: false, shouldScroll: false })
            } else {
                this.setFocusedPoint({ index, shouldScroll: false })
            }
        },

        handleCancel() {
            this.syncPoints()
        },

        async importGeoradar(event) {
            const file = head(event.target.files)
            try {
                await this.importGeoradarCSV({ file })
                this.$refs.uploadGeoradar.value = ''
                const index = this.points.findLastIndex((point) => point.errors === undefined && point.action === 'create')
                if (index !== -1) this.handleClickOnPoint({ index })
            } catch (err) {
                throw err
            }
        },

        async saveCheckedGeoradarPoints() {
            try {
                await this.saveGeoradarPoints({ points: this.pointWithAction })
            } catch (err) {
                throw err
            }
        },

        async exportGeoradarCSV() {
            if (!this.loading) {
                this.exportLoading = true
                try {
                    const csv = await this.getSavedPointsToCSV()
                    this.exportLoading = false

                    const csvFile = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const csvURL = window.URL.createObjectURL(csvFile)
                    const link = document.createElement('a')
                    link.href = csvURL
                    link.download = 'georadar_export.csv'
                    link.click()
                } catch (err) {
                    throw err
                }
            }
        }
    }
}
</script>
